import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppNav from "@/components/site/AppNav";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileText,
  ImageIcon,
  Search,
  Sparkles,
  Mail,
  Zap,
  ArrowRight,
  TrendingUp,
  Copy,
  Gift,
} from "lucide-react";

const shortcuts = [
  { to: "/workspace?kind=text&tool=blog", icon: FileText, label: "Blog post", tag: "Text" },
  { to: "/workspace?kind=text&tool=social", icon: Zap, label: "Social captions", tag: "Text" },
  { to: "/workspace?kind=text&tool=email", icon: Mail, label: "Marketing email", tag: "Text" },
  { to: "/workspace?kind=text&tool=seo", icon: Search, label: "SEO meta pack", tag: "Text" },
  { to: "/workspace?kind=text&tool=ideas", icon: Sparkles, label: "10 content ideas", tag: "Text" },
  { to: "/workspace?kind=image", icon: ImageIcon, label: "Editorial image", tag: "Image" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    api
      .get("/studio/history?limit=6")
      .then((r) => setHistory(r.data.items))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/referral/me").then((r) => setReferral(r.data)).catch(() => {});
  }, []);

  const referralLink = referral
    ? `${window.location.origin}/?ref=${referral.code}`
    : "";

  function copyReferralLink() {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied");
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <AppNav />
      <main className="max-w-[1400px] mx-auto">
        <section className="p-8 md:p-12 border-b border-border grid grid-cols-1 md:grid-cols-12 gap-0">
          <div className="md:col-span-8 md:border-r border-border md:pr-12">
            <div className="overline text-muted-foreground">Good {timeOfDay()}, {user?.name?.split(" ")[0] || "friend"}</div>
            <h1 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
              You have <span className="text-primary">{user?.credits ?? 0}</span> credits.
              <br />
              <span className="italic">Let's spend 1.</span>
            </h1>
            <p className="mt-6 max-w-xl text-muted-foreground">
              What are you promoting? Type one line in the workspace. Watch it build.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/workspace">
                <Button className="rounded-none h-12 px-6 font-mono uppercase tracking-widest text-xs" data-testid="open-workspace-btn">
                  Open workspace <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/workspace?challenge=1">
                <Button variant="outline" className="rounded-none h-12 px-6 font-mono uppercase tracking-widest text-xs" data-testid="challenge-btn">
                  Start 8-min challenge
                </Button>
              </Link>
            </div>
          </div>
          <div className="md:col-span-4 mt-10 md:mt-0 md:pl-12">
            <div className="overline text-muted-foreground">Your balance</div>
            <div className="mt-4 border border-border p-6" data-testid="plan-widget">
              <div className="flex items-baseline gap-2">
                <span className="font-serif-display text-5xl text-primary">{user?.credits ?? 0}</span>
                <span className="overline text-muted-foreground">credits</span>
              </div>
              <div className="mt-1 text-xs font-mono uppercase tracking-widest text-muted-foreground">
                Plan · {user?.plan || "free"} · {user?.plan_status}
              </div>
              <div className="rule-h my-6" />
              <div className="space-y-2 text-xs text-muted-foreground">
                <div>Text = 1c · Image = 5c · Video = 40c</div>
                <div>Credits never expire. +5 free every week.</div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3 text-xs">
                <Link to="/workspace#credit-packs" className="overline text-primary hover-invert" data-testid="topup-link">
                  Top up →
                </Link>
                <Link to="/billing" className="overline text-primary hover-invert">
                  Billing →
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-border p-8 md:p-12">
          <div className="overline text-muted-foreground mb-6">Quick generate</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border-l border-t border-border">
            {shortcuts.map(({ to, icon: Icon, label, tag }, i) => (
              <Link
                key={label}
                to={to}
                className="p-6 border-r border-b border-border group hover:bg-foreground hover:text-background transition-colors"
                data-testid={`shortcut-${i}`}
              >
                <div className="flex items-center justify-between">
                  <Icon className="w-5 h-5 text-primary" />
                  <span className="overline opacity-60">{tag}</span>
                </div>
                <div className="font-serif-display text-lg mt-6 leading-tight">{label}</div>
                <ArrowRight className="w-4 h-4 mt-4 opacity-60 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </section>

        {history.length > 0 && (
          <section className="border-b border-border p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-0">
            <div className="md:col-span-8 md:border-r border-border md:pr-12">
              <div className="flex items-center gap-2 overline text-muted-foreground">
                <Gift className="w-3 h-3" /> You just made content
              </div>
              <h2 className="font-serif-display text-3xl md:text-5xl leading-none tracking-tight mt-4">
                Want to make money
                <br />
                sharing Draftwave?
              </h2>
              <p className="mt-4 max-w-xl text-muted-foreground">
                Earn 30% recurring commission from qualifying referrals. Not a bounty — it pays as
                long as they stay subscribed.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl">
                <div
                  className="flex-1 border border-border p-3 font-mono text-sm truncate bg-background"
                  data-testid="referral-link"
                >
                  {referralLink || "Loading…"}
                </div>
                <Button
                  onClick={copyReferralLink}
                  className="rounded-none h-11 px-5 font-mono uppercase tracking-widest text-xs"
                  data-testid="copy-referral-btn"
                >
                  <Copy className="w-4 h-4" />
                  Get my referral link
                </Button>
              </div>
            </div>
            <div className="md:col-span-4 md:pl-12 mt-8 md:mt-0">
              <div className="overline text-muted-foreground">Your ledger</div>
              <div className="mt-4 grid grid-cols-2 gap-0 border-l border-t border-border">
                <div className="p-4 border-r border-b border-border">
                  <div className="font-serif-display text-3xl">{referral?.clicks ?? 0}</div>
                  <div className="overline text-muted-foreground mt-1 text-[10px]">Clicks</div>
                </div>
                <div className="p-4 border-b border-border">
                  <div className="font-serif-display text-3xl">{referral?.signups ?? 0}</div>
                  <div className="overline text-muted-foreground mt-1 text-[10px]">Signups</div>
                </div>
                <div className="p-4 border-r border-border">
                  <div className="font-serif-display text-3xl">{referral?.conversions ?? 0}</div>
                  <div className="overline text-muted-foreground mt-1 text-[10px]">Paid</div>
                </div>
                <div className="p-4">
                  <div className="font-serif-display text-3xl text-primary" data-testid="referral-earnings">
                    {referral?.earnings_display ?? "$0.00"}
                  </div>
                  <div className="overline text-muted-foreground mt-1 text-[10px]">Earned</div>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="p-8 md:p-12">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="overline text-muted-foreground">Recent work</div>
              <h2 className="font-serif-display text-3xl md:text-4xl mt-2 leading-none">Your last drafts</h2>
            </div>
            <Link to="/history" className="overline text-primary hover-invert" data-testid="view-history-link">
              View all →
            </Link>
          </div>
          {loading ? (
            <div className="text-muted-foreground overline">Loading…</div>
          ) : history.length === 0 ? (
            <div className="border border-dashed border-border p-16 text-center">
              <TrendingUp className="w-8 h-8 mx-auto text-muted-foreground" />
              <div className="font-serif-display text-2xl mt-4">Nothing here yet</div>
              <p className="text-muted-foreground mt-2 text-sm">
                Head to the Workspace and generate your first draft.
              </p>
              <Link to="/workspace" className="inline-block mt-6">
                <Button className="rounded-none h-11 px-5 font-mono uppercase tracking-widest text-xs">
                  Start writing
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-border">
              {history.map((h) => (
                <div key={h.id} className="p-6 border-r border-b border-border" data-testid={`history-tile-${h.id}`}>
                  <div className="flex items-center justify-between overline">
                    <span className="text-primary">{h.tool}</span>
                    <span className="text-muted-foreground">{h.kind}</span>
                  </div>
                  <div className="mt-4 text-sm font-medium line-clamp-2">{h.prompt}</div>
                  {h.kind === "image" ? (
                    <img
                      src={h.output}
                      alt={h.prompt}
                      className="mt-4 w-full aspect-square object-cover border border-border"
                    />
                  ) : (
                    <div className="mt-4 text-xs text-muted-foreground line-clamp-4 font-mono">
                      {h.output}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function timeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "evening";
}
